import './ActionButton.scss';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { MdMoreVert } from 'react-icons/md';
import { IBaseAction } from '@models/actions/IBaseAction';
import { IActionService } from '@services/actions/IActionService';
import { IDocBasedService } from '@services/actions/IDocBasedService';
import Menu from '@srm/ui/menu';
import Button from '@srm/ui/button';
import { useLocation, useNavigate } from 'react-router';
import { useDocumentHashContext } from '@hooks/useDocumentHashContext';
import { useDraftMonitorContext } from '@hooks/useDraftMonitorContext';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { ConfigService } from '@/configuration/services/configService';
import { IDebugSettings } from '@models/IDebugSettings';
import { convertActions } from '@utils/actionsHelpers';
import ActionDiagnosticIndicators from '@atoms/ActionDiagnosticIndicators/ActionDiagnosticIndicators';
import type { ActionDiagnosticReference } from '@/context/DocumentSupportDiagnosticsContext';

export interface IActionButtonProps {
    objId: string;
    objData?: unknown;
    service?: IActionService<IBaseAction>;
    onActionClick?: () => void;
    onModifyData?: () => void;
    /** Типы действий, которые надо скрыть из меню (напр. уже вынесенные отдельной кнопкой рядом). */
    excludeActionTypes?: unknown[];
    /** Adapter диагностической identity для action-контролов со своим источником действий. */
    getDiagnosticReference?: (action: IBaseAction) => ActionDiagnosticReference;
}

const ActionButton: React.FC<IActionButtonProps> = (props: IActionButtonProps) => {
    const {
        objData,
        objId,
        onActionClick: onActionClickProp,
        onModifyData,
        service,
        excludeActionTypes,
        getDiagnosticReference,
    } = props;
    const config = ConfigService.get();
    const [debugSettings] = useLocalStorageState<IDebugSettings>('Debug-Settings', {
        notifyFormulaErrors: config.environment !== 'PROD',
        showActionKeys: config.environment !== 'PROD',
    });

    const navigate = useNavigate();
    const location = useLocation();
    const draftMonitor = useDraftMonitorContext();
    const docHashServ = useDocumentHashContext();

    const onActionClick = useCallback(
        (act: IBaseAction) => {
            const executor = service?.getActionExecutor(act);
            if (executor) {
                executor.navigate = navigate;
                executor.location = location;
            }

            const docsBasedService = service as unknown as IDocBasedService;
            let parentId: string | undefined = undefined;
            if (docsBasedService) {
                parentId = docsBasedService.documentId;
            }

            executor?.run(
                objId,
                parentId,
                act,
                objData,
                (isSucceed) => {
                    setElement(undefined);

                    if (isSucceed && onModifyData) {
                        onModifyData();
                    }
                    draftMonitor?.resume();
                    if (docHashServ) {
                        docHashServ.resumeMonitoring();
                    }
                },
                () => {
                    draftMonitor?.pause();
                    if (docHashServ) {
                        docHashServ.pause();
                    }
                    onActionClickProp && onActionClickProp();
                    setElement(executor?.visualElement && executor?.visualElement());
                },
            );
            setElement(executor?.visualElement && executor?.visualElement());
        },
        [docHashServ, draftMonitor, location, navigate, objData, objId, onActionClickProp, onModifyData, service],
    );

    const [isLoading, setIsLoading] = useState(false);
    const [fetchedActions, setFetchedActions] = useState<IBaseAction[]>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [element, setElement] = useState<ReactElement>();

    const localActions = (objData as { actions?: IBaseAction[] } | undefined)?.actions;
    const isActionsHidden = Boolean((objData as { hideActions?: boolean } | undefined)?.hideActions);
    const baseActions = useMemo(() => {
        const acts = localActions ?? fetchedActions;
        if (!acts || !excludeActionTypes?.length) return acts;
        return acts.filter((a) => !excludeActionTypes.includes(a.type));
    }, [localActions, fetchedActions, excludeActionTypes]);
    const actItems = useMemo(
        () =>
            convertActions(
                baseActions,
                onActionClick,
                debugSettings.showActionKeys,
                config,
                getDiagnosticReference
                    ? (action) => <ActionDiagnosticIndicators references={[getDiagnosticReference(action)]} />
                    : undefined,
                { showMenuIcons: true },
            ),
        [baseActions, config, debugSettings.showActionKeys, getDiagnosticReference, onActionClick],
    );

    useEffect(() => {
        setFetchedActions(undefined);
        setIsDialogOpen(false);
    }, [objId]);

    const loadActions = useCallback(() => {
        if (localActions || fetchedActions || isLoading || !service) {
            return;
        }

        setIsLoading(true);

        service
            .fetchActions(objId)
            .then((res) => {
                setFetchedActions(res.data);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [fetchedActions, isLoading, localActions, objId, service]);

    const onOpenChange = useCallback(
        (opened: boolean) => {
            setIsDialogOpen(opened);

            if (opened) {
                loadActions();
            }
        },
        [loadActions],
    );

    return (
        <>
            {!isActionsHidden && (
                <div className="action-button">
                    <Menu
                        className="action-button__menu"
                        list={actItems}
                        loading={isLoading}
                        opened={isDialogOpen}
                        onOpenChange={onOpenChange}
                        position="left-start"
                        popoverProps={{
                            className: 'action-button__dialog',
                            hoverEndAdornment: true,
                            minWidth: 206,
                            verticalOffset: 8,
                            // кнопка — на каждой строке реестра: панель монтируется с первого открытия
                            mountOnOpen: true,
                        }}
                    >
                        <Button
                            className="action-button__trigger"
                            variant="text"
                            tone="neutral"
                            size="auto"
                            aria-label="Действия"
                            startAdornment={<MdMoreVert size="20" />}
                        />
                    </Menu>
                </div>
            )}

            {element}
        </>
    );
};

export default ActionButton;
